// web/src/components/ds/DsTooltip.tsx — DS.5 (sprint aos-ds-shell, spec §2 D5).
//
// The ONLY way a shell surface consumes `@ds/feedback/Tooltip` — the one DS
// piece at v0.2.0 that portals (vendor/venuz-design/PORTALS.md, direct). The
// adapter:
//   (a) injects the portal container: it reads DS.4's `PortalContainerContext`
//       (the `.aos-ds-root` element `DsPortalProvider` mounts) and passes it
//       as `portalContainer`, with `portal` always on — a sidebar tooltip must
//       escape the column's overflow box, and it must land INSIDE the skin's
//       root, never on `document.body` (AC-8 asserts `closest('.aos-ds-root')`
//       in a running browser; CRM measured z 10000 vs an app ceiling of 900).
//   (b) forwards `delay` (the DS piece's own prop; Radix's `delayDuration`).
//   (c) `data-testid` (and any other HTML attribute) lands on the WRAPPER span
//       — the vendored piece spreads its props there and exposes no popup
//       attribute slot, so a test reads the popup by `role="tooltip"`
//       (WorkspaceAvatar.test.tsx, the one caller that read the old id).
//   (d) restores Radix's Escape dismissal: the DS piece hides only on
//       mouseleave/blur, so a document `keydown` listener dispatches a
//       bubbling `focusout` on the wrapper — React derives the piece's
//       `onBlur` from it — WITHOUT moving focus (the trigger keeps it).
//
// `content` is a string by the piece's contract — a structured tooltip body
// (RunnersHealthPill's per-runner rows) is not expressible here and stays on
// the Radix primitive as a named residue (results doc).

import { useContext, useEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import Tooltip from '@ds/feedback/Tooltip';
import { PortalContainerContext } from '@ds/primitives/portal-container';

type TooltipProps = ComponentProps<typeof Tooltip>;

export interface DsTooltipProps
  extends Omit<TooltipProps, 'portal' | 'portalContainer' | 'children' | 'content'> {
  content: string;
  children: ReactNode;
}

export function DsTooltip({ content, children, delay = 300, ...rest }: DsTooltipProps) {
  const container = useContext(PortalContainerContext);
  const wrapperRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = wrapperRef.current;
      if (!el) return;
      el.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Tooltip ref={wrapperRef} content={content} delay={delay} portal portalContainer={container} {...rest}>
      {children}
    </Tooltip>
  );
}

export default DsTooltip;
